/**
 * Salesforce REST API Client
 *
 * All calls go through /api/sf proxy which handles:
 * - PIN verification against Supabase staff table
 * - Client Credentials auth to Salesforce
 * - X-Medewerker-Id header injection
 */

import { useAuthStore } from '../../stores/auth-store';
import type {
  SalesforceApiResponse,
  WerkOrder,
  WasserijItem,
  Foto,
  Route,
  RoutesObject,
  GeplandeRit,
  RouteWithStops,
  GeplandeRitWithDetails,
  CompleteStopRequest,
  CompleteStopResponse,
  SkipStopRequest,
  PartialPickupRequest,
  DailyStats,
  ProductCatalog,
  PricePreviewRequest,
  PricePreviewResponse,
  CreateItemRequest,
  CreateItemResponse,
  CreateTransactionRequest,
  CreateTransactionResponse,
  ItemEditOptions,
  ItemEditResponse,
  PreparationRoutesResponse,
  PreparationItemsResponse,
  TogglePreparationRequest,
  TogglePreparationResponse,
  BusLoadingRoutesResponse,
  BusLoadingItemsResponse,
  ToggleLoadingRequest,
  ToggleLoadingResponse,
  UnderlayItemsResponse,
  ToggleUnderlayRequest,
  ToggleUnderlayResponse,
  VoorinspectieItemsResponse,
  SaveVoorinspectieRequest,
  SaveInspectionResponse,
  RisicoMeldRequest,
  RisicoMeldResponse,
  AddKlantEigenItemRequest,
  AddKlantEigenItemResponse,
  KlachtMeldRequest,
  KlachtMeldResponse,
  KlachtActionType,
  KlachtActionResponse,
  ReinigenItemsResponse,
  SaveReinigenRequest,
  NaInspectieItemsResponse,
  SaveNaInspectieRequest,
  SaveNaInspectieResponse,
  SelfServiceWorkOrdersResponse,
  SelfServiceFilter,
  CompleteSelfServiceResponse,
  WebshopInpakkenOrder,
  WebshopInpakkenOrdersResponse,
  MarkPackedResponse,
  ZoekKledenResponse,
} from './types';

export class SalesforceApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly errorCode?: string,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'SalesforceApiError';
  }
}

// Auth error event handlers - allows components to react to auth failures
type AuthErrorHandler = () => void;
const authErrorHandlers: Set<AuthErrorHandler> = new Set();

/**
 * Subscribe to auth error events (session expired, 401, etc.)
 * Returns unsubscribe function
 */
export function onAuthError(handler: AuthErrorHandler): () => void {
  authErrorHandlers.add(handler);
  return () => authErrorHandlers.delete(handler);
}

/**
 * Emit auth error to all subscribers
 */
function emitAuthError(): void {
  console.log('[SalesforceClient] Emitting auth error to', authErrorHandlers.size, 'handlers');
  authErrorHandlers.forEach(handler => {
    try {
      handler();
    } catch (e) {
      console.error('[SalesforceClient] Auth error handler failed:', e);
    }
  });
}

// ── Circuit-breaker voor de SF-daglimiet ────────────────────────────────────
// Zodra Salesforce REQUEST_LIMIT_EXCEEDED ("TotalRequests Limit exceeded") meldt,
// pauzeren de achtergrond-pollers (inpakken-poll + push-sync) hun calls tot dit
// venster verloopt. Voorkomt dat de field-app de SF-API blijft hameren tijdens
// een limiet-storing — dat voedt anders de retry-storm. Door gebruiker gestarte
// acties (route laden, stop afmelden) blijven gewoon proberen.
const RATE_LIMIT_BACKOFF_MS = 5 * 60 * 1000;
let rateLimitedUntil = 0;

/** True zolang we in de back-off na een SF-daglimiet (REQUEST_LIMIT_EXCEEDED) zitten. */
export function isSalesforceRateLimited(): boolean {
  return Date.now() < rateLimitedUntil;
}

function noteRateLimitHit(): void {
  rateLimitedUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
  console.warn(
    `[SalesforceClient] SF daglimiet bereikt — achtergrond-sync pauzeert ${RATE_LIMIT_BACKOFF_MS / 1000}s`
  );
}

/**
 * Klaarleggen en Bus inpakken krijgen de stoprichting binnen als `type_x`, niet
 * als `type`. Die sleutel staat LETTERLIJK zo in de JSON (port-regel in
 * `route-klaarleg-reads.ts`, empirisch geverifieerd tegen prod op 30-08): de
 * Apex-DTO belooft een rename die nergens gebeurt. Zonder deze normalisatie
 * leest de UI `stop.type` als undefined en valt het richting-vak stil terug op
 * de afleveren-kleur zonder tekst.
 *
 * Eén plek voor beide endpoints, zodat een derde lezer dezelfde val niet opnieuw
 * inloopt. `type` wint als de backend hem ooit alsnog gaat sturen.
 */
function normaliseerStopRichting<T>(
  response: SalesforceApiResponse<T>
): SalesforceApiResponse<T> {
  const stops = (response.data as { stops?: unknown } | undefined)?.stops;
  if (!Array.isArray(stops)) return response;
  for (const stop of stops as Array<Record<string, unknown>>) {
    if (stop == null || typeof stop !== 'object') continue;
    if (stop.type == null && stop.type_x != null) stop.type = stop.type_x;
  }
  return response;
}

class SalesforceClient {
  /**
   * Call an Apex REST endpoint via /api/sf proxy
   * Proxy handles PIN verification + Client Credentials + X-Medewerker-Id
   */
  async callApexRest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
    body?: unknown
  ): Promise<T> {
    const { staff, pin } = useAuthStore.getState();
    if (!staff || !pin) {
      emitAuthError();
      throw new SalesforceApiError('Not authenticated', 401, 'NOT_AUTHENTICATED');
    }

    // Werken is activiteit: de inactiviteitsklok van de sessie loopt mee met de
    // calls die de chauffeur veroorzaakt, niet met de levensduur van het
    // tabblad. Schrijft hooguit eens per minuut.
    useAuthStore.getState().raakSessieAan();

    const sfPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    console.log('[SalesforceClient] API call:', method, sfPath);

    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Staff-Id': staff.id,
        'X-Staff-Pin': pin,
        'X-SF-Path': sfPath,
      },
    };

    if (body && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch('/api/sf', fetchOptions);

    // Uitloggen mag alléén bij een écht geweigerde PIN. Elke andere 401 die
    // hier ooit binnenkwam (Supabase-hik in de PIN-controle, geweigerde
    // org-token van Salesforce) zette de chauffeur midden op de route terug op
    // het loginscherm voor een storing waar opnieuw inloggen niets aan
    // verandert. De proxies markeren een echte PIN-weigering met PIN_INVALID;
    // de rest komt binnen als 502/503 en wordt hier een gewone fout waar de
    // offline-indicator en de sync-retry hun werk op doen.
    if (response.status === 401) {
      const body = await response.json().catch(() => null) as { code?: string; error?: string } | null;
      if (body?.code === 'PIN_INVALID') {
        emitAuthError();
        throw new SalesforceApiError('Ongeldige PIN', 401, 'PIN_INVALID', body);
      }
      throw new SalesforceApiError(
        body?.error || 'Authenticatie tijdelijk niet mogelijk',
        503,
        'AUTH_BACKEND',
        body
      );
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      // SF-daglimiet → circuit-breaker openen zodat achtergrond-pollers pauzeren.
      if (
        response.status === 403 &&
        /REQUEST_LIMIT_EXCEEDED|TotalRequests Limit/i.test(JSON.stringify(errorBody))
      ) {
        noteRateLimitHit();
      }
      throw new SalesforceApiError(
        errorBody.message || errorBody.error || 'API request failed',
        response.status,
        errorBody.errorCode,
        errorBody
      );
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return {} as T;
    }

    return response.json();
  }

  // ============================================
  // Legacy CRUD stubs (used by db/sync-engine.ts)
  // These proxy through Apex REST — kept for backwards compat
  // ============================================

  async create<T extends Record<string, unknown>>(
    sobject: string,
    data: T
  ): Promise<{ id: string; success: boolean; errors: unknown[] }> {
    return this.callApexRest(`/v1/field/crud/${sobject}`, 'POST', data);
  }

  async update<T extends Record<string, unknown>>(
    sobject: string,
    id: string,
    data: T
  ): Promise<void> {
    return this.callApexRest(`/v1/field/crud/${sobject}/${id}`, 'PATCH', data);
  }

  async delete(sobject: string, id: string): Promise<void> {
    return this.callApexRest(`/v1/field/crud/${sobject}/${id}`, 'DELETE');
  }

  // ============================================
  // Kleda Field App Specific Methods
  // ============================================

  /**
   * Get routes for the current user (driver)
   */
  async getMyRoutes(date?: string): Promise<SalesforceApiResponse<Route[]>> {
    const dateParam = date || new Date().toISOString().split('T')[0];
    return this.callApexRest<SalesforceApiResponse<Route[]>>(
      `/v1/field/routes?date=${dateParam}`
    );
  }

  /**
   * Get work orders for a specific route
   */
  async getRouteWorkOrders(routeId: string): Promise<SalesforceApiResponse<WerkOrder[]>> {
    return this.callApexRest<SalesforceApiResponse<WerkOrder[]>>(
      `/v1/field/routes/${routeId}/workorders`
    );
  }

  /**
   * Get a single work order with all related data
   */
  async getWorkOrder(workOrderId: string): Promise<SalesforceApiResponse<WerkOrder>> {
    return this.callApexRest<SalesforceApiResponse<WerkOrder>>(
      `/v1/field/workorders/${workOrderId}`
    );
  }

  /**
   * Update work order status
   */
  async updateWorkOrderStatus(
    workOrderId: string,
    status: string,
    notes?: string
  ): Promise<SalesforceApiResponse<WerkOrder>> {
    return this.callApexRest<SalesforceApiResponse<WerkOrder>>(
      `/v1/field/workorders/${workOrderId}/status`,
      'POST',
      { status, notes }
    );
  }

  /**
   * Get wasserij items for a work order
   */
  async getWasserijItems(workOrderId: string): Promise<SalesforceApiResponse<WasserijItem[]>> {
    return this.callApexRest<SalesforceApiResponse<WasserijItem[]>>(
      `/v1/field/workorders/${workOrderId}/items`
    );
  }

  /**
   * Update a wasserij item (e.g., after inspection)
   */
  async updateWasserijItem(
    itemId: string,
    data: Partial<WasserijItem>
  ): Promise<SalesforceApiResponse<WasserijItem>> {
    return this.callApexRest<SalesforceApiResponse<WasserijItem>>(
      `/v1/field/items/${itemId}`,
      'PATCH',
      data
    );
  }

  /**
   * Complete inspection for a wasserij item
   */
  async completeInspection(
    itemId: string,
    inspectionData: {
      condition: string;
      notes?: string;
      dimensions?: { length?: number; width?: number; diameter?: number };
    }
  ): Promise<SalesforceApiResponse<WasserijItem>> {
    return this.callApexRest<SalesforceApiResponse<WasserijItem>>(
      `/v1/field/items/${itemId}/inspection`,
      'POST',
      inspectionData
    );
  }

  /**
   * Upload a photo reference (URL from CompanyCam or other source)
   */
  async createPhotoRecord(photoData: {
    url: string;
    category: Foto['Categorie__c'];
    workOrderId?: string;
    wasserijItemId?: string;
    description?: string;
  }): Promise<SalesforceApiResponse<Foto>> {
    return this.callApexRest<SalesforceApiResponse<Foto>>(
      '/v1/field/photos',
      'POST',
      {
        URL__c: photoData.url,
        Categorie__c: photoData.category,
        Bron__c: 'API',
        Werk_Order__c: photoData.workOrderId,
        Wasserij_item__c: photoData.wasserijItemId,
        Beschrijving__c: photoData.description,
      }
    );
  }

  /**
   * Get photos for a work order or item
   */
  async getPhotos(params: {
    workOrderId?: string;
    wasserijItemId?: string;
  }): Promise<SalesforceApiResponse<Foto[]>> {
    const queryParams = new URLSearchParams();
    if (params.workOrderId) queryParams.set('workOrderId', params.workOrderId);
    if (params.wasserijItemId) queryParams.set('itemId', params.wasserijItemId);

    return this.callApexRest<SalesforceApiResponse<Foto[]>>(
      `/v1/field/photos?${queryParams.toString()}`
    );
  }

  /**
   * Mark work order as picked up / delivered
   */
  async completeStop(
    workOrderId: string,
    type: 'pickup' | 'delivery',
    data: {
      signature?: string;
      notes?: string;
      completedAt?: string;
    }
  ): Promise<SalesforceApiResponse<WerkOrder>> {
    return this.callApexRest<SalesforceApiResponse<WerkOrder>>(
      `/v1/field/workorders/${workOrderId}/complete`,
      'POST',
      { type, ...data }
    );
  }

  // ============================================
  // Routes__c and Geplande_Rit__c Methods
  // ============================================

  /**
   * Get driver's routes for a specific date with all stops
   * Uses Apex REST API - current user is determined server-side via UserInfo.getUserId()
   */
  async getDriverRoutes(
    date: string
  ): Promise<SalesforceApiResponse<RouteWithStops[]>> {
    return this.callApexRest<SalesforceApiResponse<RouteWithStops[]>>(
      `/v1/field/driver/routes?date=${date}`
    );
  }

  /**
   * Get driver's routes for a date range (for week view)
   */
  async getDriverRoutesRange(
    startDate: string,
    endDate: string
  ): Promise<SalesforceApiResponse<RoutesObject[]>> {
    return this.callApexRest<SalesforceApiResponse<RoutesObject[]>>(
      `/v1/field/driver/routes?startDate=${startDate}&endDate=${endDate}`
    );
  }

  /**
   * Get stops (Geplande_Rit__c) for a specific route
   * Returns stops ordered by Route_Sequence__c with expanded Werk_Order data
   */
  async getRouteStops(routeId: string): Promise<SalesforceApiResponse<GeplandeRitWithDetails[]>> {
    return this.callApexRest<SalesforceApiResponse<GeplandeRitWithDetails[]>>(
      `/v1/field/routes/${routeId}/stops`
    );
  }

  /**
   * Complete a stop (Geplande_Rit__c)
   * Updates both Geplande_Rit__c.Status__c and Werk_Order__c.Werk_Order_Status__c
   * - Ophalen: Sets Werk_Order_Status__c = 'Opgehaald'
   * - Terugbrengen: Sets Werk_Order_Status__c = 'Teruggebracht'
   *
   * Returns error if:
   * - Chauffeur mismatch (CHAUFFEUR_MISMATCH)
   * - Terugbrengen attempted before Ophalen completed (OPHALEN_NOT_COMPLETED)
   * - Stop already completed (ALREADY_COMPLETED)
   */
  async completeGeplandeRit(
    request: CompleteStopRequest
  ): Promise<CompleteStopResponse> {
    return this.callApexRest<CompleteStopResponse>(
      `/v1/field/stops/${request.ritId}/complete`,
      'POST',
      request
    );
  }

  /**
   * Skip a stop with a reason
   */
  async skipGeplandeRit(request: SkipStopRequest): Promise<SalesforceApiResponse<GeplandeRit>> {
    return this.callApexRest<SalesforceApiResponse<GeplandeRit>>(
      `/v1/field/stops/${request.ritId}/skip`,
      'POST',
      request
    );
  }

  /**
   * Heropenen: een overgeslagen stop terug naar 'Ingepland' (klant belde terug,
   * kleed wordt alsnog gehaald of gebracht). Eigen pad in de office-app, geen Apex.
   */
  async heropenGeplandeRit(ritId: string): Promise<SalesforceApiResponse<GeplandeRit>> {
    return this.callApexRest<SalesforceApiResponse<GeplandeRit>>(
      `/v1/field/stops/${ritId}/heropen`,
      'POST',
      {}
    );
  }

  /**
   * Vertrek vastleggen na de vertrekcontrole (plan 2026-10-12): anker voor de
   * herijk vóór de eerste stop. Eigen pad in de office-app, geen Apex;
   * idempotent (eerste moment wint).
   */
  async vertrekRoute(routeId: string, departedAt: string): Promise<SalesforceApiResponse<{ routeId: string; vertrokkenOp: string; alVertrokken: boolean }>> {
    return this.callApexRest<SalesforceApiResponse<{ routeId: string; vertrokkenOp: string; alVertrokken: boolean }>>(
      `/v1/field/routes/${routeId}/vertrek`,
      'POST',
      { departedAt }
    );
  }

  /**
   * Deels opgehaald: markeer welke kleden zijn meegenomen; de rest blijft staan
   * en de WO gaat naar de na-ophaal-staat (office plant een extra ophaalrit).
   */
  async partialPickupGeplandeRit(
    request: PartialPickupRequest
  ): Promise<SalesforceApiResponse<GeplandeRit>> {
    return this.callApexRest<SalesforceApiResponse<GeplandeRit>>(
      `/v1/field/stops/${request.ritId}/partial`,
      'POST',
      request
    );
  }

  /**
   * Reparatie cargo voor een reparateur-stop (heen / terug / liggen er nog).
   * Lege secties bij niet-reparateur-stops.
   */
  async getReparatieCargo(ritId: string): Promise<SalesforceApiResponse<{
    geplandeRitId?: string | null;
    reparateurId?: string | null;
    reparateurNaam?: string | null;
    heen: Array<Record<string, unknown>>;
    terug: Array<Record<string, unknown>>;
    liggenErNog: Array<Record<string, unknown>>;
  }>> {
    return this.callApexRest(`/v1/field/stops/${ritId}/reparatie-cargo`);
  }

  /**
   * Markeer een reparatie-kleed als afgeleverd bij de reparateur.
   * `bron='Field-App'` wordt server-side gezet.
   */
  async markReparatieKleedAfgeleverd(wiId: string): Promise<SalesforceApiResponse<{
    wiId: string;
    alreadyAccepted?: boolean;
  }>> {
    return this.callApexRest(
      `/v1/field/wi/${wiId}/reparatie-afgeleverd`,
      'POST',
      {}
    );
  }

  /**
   * Markeer een reparatie-kleed als opgehaald bij de reparateur.
   */
  async markReparatieKleedOpgehaald(wiId: string): Promise<SalesforceApiResponse<{
    wiId: string;
    alreadyAccepted?: boolean;
  }>> {
    return this.callApexRest(
      `/v1/field/wi/${wiId}/reparatie-opgehaald`,
      'POST',
      {}
    );
  }

  /**
   * Finalize a route - move all 'Opgehaald' items to 'Klaar voor Voorinspectie'
   * Called when driver returns to wasserij and unloads the bus.
   *
   * @param routeId - The Route__c Id
   * @returns Response with count of items updated, plus het totaal opgehaalde
   *   kleden van de route (werkbank #102: de melding telde alleen de laatste stap)
   */
  async finalizeRoute(routeId: string): Promise<SalesforceApiResponse<{
    routeId: string;
    routeName: string;
    itemsUpdated: number;
    itemsOpgehaaldTotaal?: number;
    finalized?: boolean;
    message?: string;
  }>> {
    return this.callApexRest<SalesforceApiResponse<{
      routeId: string;
      routeName: string;
      itemsUpdated: number;
      itemsOpgehaaldTotaal?: number;
      finalized?: boolean;
      message?: string;
    }>>(
      `/v1/field/routes/${routeId}/finalize`,
      'POST',
      {}
    );
  }

  /**
   * Get daily stats for the driver dashboard
   * Uses Apex REST API - current user is determined server-side via UserInfo.getUserId()
   */
  async getDailyStats(date: string): Promise<SalesforceApiResponse<DailyStats>> {
    return this.callApexRest<SalesforceApiResponse<DailyStats>>(
      `/v1/field/driver/stats?date=${date}`
    );
  }

  /**
   * Validate if a Terugbrengen stop can be completed
   * Checks if the corresponding Ophalen for the same Werk_Order is completed
   */
  async validateTerugbrengen(ritId: string): Promise<SalesforceApiResponse<{
    canComplete: boolean;
    reason?: string;
    ophalenRitId?: string;
    ophalenStatus?: string;
  }>> {
    return this.callApexRest<SalesforceApiResponse<{
      canComplete: boolean;
      reason?: string;
      ophalenRitId?: string;
      ophalenStatus?: string;
    }>>(`/v1/field/stops/${ritId}/validate-terugbrengen`);
  }

  /**
   * Batch complete multiple stops at the same address
   */
  async batchCompleteStops(
    ritIds: string[],
    notes?: string
  ): Promise<SalesforceApiResponse<{
    completed: string[];
    failed: { ritId: string; error: string }[];
  }>> {
    return this.callApexRest<SalesforceApiResponse<{
      completed: string[];
      failed: { ritId: string; error: string }[];
    }>>(
      '/v1/field/stops/batch-complete',
      'POST',
      { ritIds, notes }
    );
  }

  // ============================================
  // Product Catalog & Add Item Methods
  // ============================================

  /**
   * Get product catalog for adding new items
   * Returns primary products, packages, and extras with pricing
   * Uses Dienst_Postcode_Associatie from the werk order for regiotoeslag pricing
   * @param werkOrderId - The werk order ID to get pricing context (regiotoeslag)
   */
  async getProductCatalog(werkOrderId: string): Promise<SalesforceApiResponse<ProductCatalog>> {
    return this.callApexRest<SalesforceApiResponse<ProductCatalog>>(
      `/v1/field/products/catalog?werkOrderId=${werkOrderId}`
    );
  }

  /**
   * Calculate price preview for a new item
   * Returns itemized pricing including primary, package, and extras
   */
  async calculatePricePreview(
    request: PricePreviewRequest
  ): Promise<SalesforceApiResponse<PricePreviewResponse>> {
    return this.callApexRest<SalesforceApiResponse<PricePreviewResponse>>(
      '/v1/field/items/price-preview',
      'POST',
      request
    );
  }

  /**
   * Create a new wasserij item with optional package and extras
   * Automatically recalculates bundle pricing after creation
   */
  async createWasserijItem(
    werkOrderId: string,
    request: CreateItemRequest
  ): Promise<CreateItemResponse> {
    return this.callApexRest<CreateItemResponse>(
      `/v1/field/workorders/${werkOrderId}/items`,
      'POST',
      request
    );
  }

  /**
   * Create a new transaction (payment) for a work order
   * Used in the delivery flow to register Pin/Contant payments
   */
  async createTransaction(
    request: CreateTransactionRequest
  ): Promise<CreateTransactionResponse> {
    return this.callApexRest<CreateTransactionResponse>(
      '/v1/field/transactions',
      'POST',
      request
    );
  }

  // ============================================
  // Item Edit Methods (for Edit Item flow)
  // ============================================

  /**
   * Get available edit options for a primary item
   * Returns available packages, available extras, current package, and current extras
   */
  async getItemEditOptions(
    itemId: string
  ): Promise<SalesforceApiResponse<ItemEditOptions>> {
    return this.callApexRest<SalesforceApiResponse<ItemEditOptions>>(
      `/v1/field/items/${itemId}/edit-options`
    );
  }

  /**
   * Replace the package on a primary item
   * Deletes the old package and creates a new one
   */
  async replaceItemPackage(
    itemId: string,
    packageProductId: string
  ): Promise<SalesforceApiResponse<ItemEditResponse>> {
    return this.callApexRest<SalesforceApiResponse<ItemEditResponse>>(
      `/v1/field/items/${itemId}/package`,
      'PUT',
      { packageProductId }
    );
  }

  /**
   * Add extras to an existing primary item
   */
  async addItemExtras(
    itemId: string,
    extraProductIds: string[]
  ): Promise<SalesforceApiResponse<ItemEditResponse>> {
    return this.callApexRest<SalesforceApiResponse<ItemEditResponse>>(
      `/v1/field/items/${itemId}/extras`,
      'POST',
      { extraProductIds }
    );
  }

  /**
   * Remove an extra from a primary item
   */
  async removeItemExtra(
    itemId: string,
    extraItemId: string
  ): Promise<SalesforceApiResponse<ItemEditResponse>> {
    return this.callApexRest<SalesforceApiResponse<ItemEditResponse>>(
      `/v1/field/items/${itemId}/extras/${extraItemId}`,
      'DELETE'
    );
  }

  // ============================================
  // Route Preparation Methods (Routes Klaarleggen)
  // ============================================

  /**
   * Get routes available for preparation (Locked__c = true)
   * @param date - Date in YYYY-MM-DD format
   */
  async getPreparationRoutes(
    date: string
  ): Promise<SalesforceApiResponse<PreparationRoutesResponse>> {
    return this.callApexRest<SalesforceApiResponse<PreparationRoutesResponse>>(
      `/v1/field/preparation/routes?date=${date}`
    );
  }

  /**
   * Get items for a route that need preparation
   * Returns items grouped by stop, sorted by Route_Sequence__c
   */
  async getPreparationItems(
    routeId: string
  ): Promise<SalesforceApiResponse<PreparationItemsResponse>> {
    const response = await this.callApexRest<SalesforceApiResponse<PreparationItemsResponse>>(
      `/v1/field/preparation/routes/${routeId}/items`
    );
    return normaliseerStopRichting(response);
  }

  /**
   * Toggle preparation status for one or more items
   * Sets Klaargelegd__c and updates Status__c to "Klaargelegd" when prepared
   */
  async toggleItemsPrepared(
    request: TogglePreparationRequest
  ): Promise<TogglePreparationResponse> {
    return this.callApexRest<TogglePreparationResponse>(
      '/v1/field/preparation/items/toggle',
      'POST',
      request
    );
  }

  // ==========================================================================
  // BUS LOADING API METHODS (Bus Inpakken)
  // ==========================================================================

  /**
   * Get routes available for bus loading (Locked__c = true)
   * @param date - Date in YYYY-MM-DD format
   */
  async getBusLoadingRoutes(
    date: string
  ): Promise<SalesforceApiResponse<BusLoadingRoutesResponse>> {
    return this.callApexRest<SalesforceApiResponse<BusLoadingRoutesResponse>>(
      `/v1/field/busloading/routes?date=${date}`
    );
  }

  /**
   * Get items for a route that need to be loaded into the bus
   * Returns items grouped by stop, sorted by Route_Sequence__c DESC (last stop first)
   */
  async getBusLoadingItems(
    routeId: string
  ): Promise<SalesforceApiResponse<BusLoadingItemsResponse>> {
    const response = await this.callApexRest<SalesforceApiResponse<BusLoadingItemsResponse>>(
      `/v1/field/busloading/routes/${routeId}/items`
    );
    return normaliseerStopRichting(response);
  }

  /**
   * Toggle loading status for one or more items
   * Sets Ingeladen__c and updates Status__c to "In bus" when loaded
   */
  async toggleItemsLoaded(
    request: ToggleLoadingRequest
  ): Promise<ToggleLoadingResponse> {
    return this.callApexRest<ToggleLoadingResponse>(
      '/v1/field/busloading/items/toggle',
      'POST',
      request
    );
  }

  // ==========================================================================
  // UNDERLAY API METHODS (Ondertapijt Maken)
  // ==========================================================================

  /**
   * Get underlay items that need to be made
   * Returns items where Product__r.ProductCode is een Kleda Base ondertapijt-SKU
   * ('WAS-UPSELL-KLEDA-BASE' of 'SHS-ONDER-KLEDA-BASE') en Klaar_voor_aflevering__c = false
   */
  async getUnderlayItems(): Promise<SalesforceApiResponse<UnderlayItemsResponse>> {
    return this.callApexRest<SalesforceApiResponse<UnderlayItemsResponse>>(
      '/v1/field/underlay/items'
    );
  }

  /**
   * Toggle underlay completion status for one or more items
   * Sets Klaar_voor_aflevering__c = true when complete
   */
  async toggleUnderlayComplete(
    request: ToggleUnderlayRequest
  ): Promise<ToggleUnderlayResponse> {
    return this.callApexRest<ToggleUnderlayResponse>(
      '/v1/field/underlay/items/toggle',
      'POST',
      request
    );
  }

  // ==========================================================================
  // VOORINSPECTIE API METHODS
  // ==========================================================================

  /**
   * Get items awaiting voorinspectie
   * Returns items where Voorinspectie_gedaan__c = false AND Status__c = 'Klaar voor Voorinspectie'
   */
  async getVoorinspectieItems(): Promise<SalesforceApiResponse<VoorinspectieItemsResponse>> {
    return this.callApexRest<SalesforceApiResponse<VoorinspectieItemsResponse>>(
      '/v1/field/voorinspectie/items'
    );
  }

  /**
   * Save voorinspectie data for a wasserij item
   * @param itemId - The Wasserij_Item__c Id
   * @param data - The voorinspectie data to save
   */
  async saveVoorinspectie(
    itemId: string,
    data: SaveVoorinspectieRequest
  ): Promise<SalesforceApiResponse<SaveInspectionResponse>> {
    return this.callApexRest<SalesforceApiResponse<SaveInspectionResponse>>(
      `/v1/field/items/${itemId}/inspection`,
      'POST',
      data
    );
  }

  /**
   * Meld een risico op een Wasserij_item.
   *
   * Loopt sinds het versimpelde risico-proces (v2) NIET meer via Apex maar via
   * de office-app, die het eigen risico-domein schrijft en daarna de
   * WI-risicovelden zet. De proxy (`/api/risico-meld`) verifieert de PIN.
   *
   * De aanroep is bewust blokkerend: pas bij success is de melding echt
   * vastgelegd. Bij een mislukte write-through komt er een fout terug zodat de
   * junior het opnieuw kan proberen, in plaats van een lege bevestiging te
   * krijgen terwijl het kleed gewoon doorloopt naar reinigen.
   */
  async meldRisico(
    itemId: string,
    data: RisicoMeldRequest
  ): Promise<SalesforceApiResponse<RisicoMeldResponse>> {
    const { staff, pin } = useAuthStore.getState();
    if (!staff || !pin) {
      return { success: false, message: 'Niet ingelogd' };
    }
    try {
      const response = await fetch('/api/risico-meld', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Staff-Id': staff.id,
          'X-Staff-Pin': pin,
        },
        body: JSON.stringify({
          wasserijItemId: itemId,
          risicoTypes: data.risicoTypes,
          toelichting: data.toelichting ?? null,
        }),
      });
      return (await response.json()) as SalesforceApiResponse<RisicoMeldResponse>;
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Risico melden mislukt',
      };
    }
  }

  /**
   * Tag een LOS, KLANT-EIGEN item à €0 op order-niveau (ondertapijt, spanbanden, …):
   * niet op de factuur, gaat netjes mee terug (KledaUpsellService, bron 'Field-App').
   * Geeft het nieuwe WI-nummer terug zodat het item gelabeld kan worden.
   */
  async addKlantEigenItem(
    workOrderId: string,
    data: AddKlantEigenItemRequest
  ): Promise<SalesforceApiResponse<AddKlantEigenItemResponse>> {
    return this.callApexRest<SalesforceApiResponse<AddKlantEigenItemResponse>>(
      `/v1/field/workorders/${workOrderId}/klant-eigen-item`,
      'POST',
      data
    );
  }

  // ============================================================================
  // Reparatie-intent (pre-pickup klantverzoek) — field-app bevestigt of weerlegt
  // Bron is server-side hard-coded op 'Field-App'.
  // ============================================================================

  async confirmReparatieIntent(
    wiId: string,
    notitie?: string
  ): Promise<SalesforceApiResponse<unknown>> {
    return this.callApexRest<SalesforceApiResponse<unknown>>(
      '/v1/field/reparatie-intent/confirm',
      'POST',
      { wiId, notitie }
    );
  }

  async denyReparatieIntent(
    wiId: string,
    notitie?: string
  ): Promise<SalesforceApiResponse<unknown>> {
    return this.callApexRest<SalesforceApiResponse<unknown>>(
      '/v1/field/reparatie-intent/deny',
      'POST',
      { wiId, notitie }
    );
  }

  // ============================================================================
  // Klacht-flow (Fase 2) — field-app meldt klacht en voegt acties toe
  // Bron is server-side hard-coded op 'Field-App'.
  // ============================================================================

  /**
   * Maak een nieuwe klacht aan op een Werk_Order via KlachtService.
   * Gebruikt door KlachtMeldDialog (per WI of per WO) en TapijtMeeRetourDialog.
   */
  async meldKlacht(
    werkOrderId: string,
    data: KlachtMeldRequest
  ): Promise<SalesforceApiResponse<KlachtMeldResponse>> {
    return this.callApexRest<SalesforceApiResponse<KlachtMeldResponse>>(
      '/v1/field/klacht/register',
      'POST',
      { originalWerkOrderId: werkOrderId, ...data }
    );
  }

  /**
   * Voeg een actie toe aan een bestaande klacht.
   * Field-app mag alleen 'NewWerkOrder' of 'NoAction'. Andere types geven
   * errorCode='ACTION_NOT_ALLOWED_FROM_FIELD' terug.
   */
  async addKlachtAction(
    klachtId: string,
    type: KlachtActionType,
    params: Record<string, unknown>
  ): Promise<SalesforceApiResponse<KlachtActionResponse>> {
    return this.callApexRest<SalesforceApiResponse<KlachtActionResponse>>(
      `/v1/field/klacht/${klachtId}/actions`,
      'POST',
      { type, params }
    );
  }

  // ============================================================================
  // Reinigen (Cleaning Process) Methods
  // ============================================================================

  /**
   * Get items awaiting reinigen (cleaning)
   * Returns items where Status__c = 'Klaar voor Reinigen'
   * (Apex-filter in KledaFieldAppRest.getReinigenItems; hier stond eerder
   * ten onrechte 'Drogen')
   */
  async getReinigenItems(): Promise<SalesforceApiResponse<ReinigenItemsResponse>> {
    return this.callApexRest<SalesforceApiResponse<ReinigenItemsResponse>>(
      '/v1/field/reinigen/items'
    );
  }

  /**
   * Save reinigen data for a wasserij item
   * @param itemId - The Wasserij_Item__c Id
   * @param data - The reinigen data to save (hoeReinigen, wasrapport notes)
   */
  async saveReinigen(
    itemId: string,
    data: SaveReinigenRequest
  ): Promise<SalesforceApiResponse<{ itemId: string; updated: boolean }>> {
    return this.callApexRest<SalesforceApiResponse<{ itemId: string; updated: boolean }>>(
      `/v1/field/items/${itemId}/reinigen`,
      'POST',
      data
    );
  }

  // ============================================================================
  // Na-inspectie (Post-inspection Quality Control) Methods
  // ============================================================================

  /**
   * Get items awaiting na-inspectie (quality control)
   * Returns items where Status__c IN ('Drogen', 'In Nabehandeling', 'Randen Plakken', 'In Reparatie')
   * AND Product__r.Wasserij_item__c = true
   */
  async getNaInspectieItems(): Promise<SalesforceApiResponse<NaInspectieItemsResponse>> {
    return this.callApexRest<SalesforceApiResponse<NaInspectieItemsResponse>>(
      '/v1/field/na-inspectie/items'
    );
  }

  /**
   * Zoek een kleed op WI-nummer of werkordernummer.
   *
   * Read-only: geeft status, werkorder, klant en de reden waarom het kleed wel
   * of niet in een werklijst staat. Loopt niet via Salesforce maar via de
   * office-app (mirror-first); de proxy routeert `/v1/field/items/search`
   * daarheen.
   */
  async zoekKleden(query: string): Promise<SalesforceApiResponse<ZoekKledenResponse>> {
    return this.callApexRest<SalesforceApiResponse<ZoekKledenResponse>>(
      `/v1/field/items/search?q=${encodeURIComponent(query)}`
    );
  }

  /**
   * Save na-inspectie data for a wasserij item
   * @param itemId - The Wasserij_Item__c Id
   * @param data - The na-inspectie data (actie, opmerkingen, etc.)
   *
   * Actions update Status__c as follows:
   * - nabehandelen -> "In Nabehandeling"
   * - nogmaals_reinigen -> "Klaar voor Reinigen"
   * - randen_plakken -> "Randen Plakken"
   * - naar_reparatie -> "In Reparatie"
   * - inpakken -> "Ingepakt" + Klaar_voor_aflevering__c = true + Datum_ingepakt__c = TODAY()
   */
  async saveNaInspectie(
    itemId: string,
    data: SaveNaInspectieRequest
  ): Promise<SalesforceApiResponse<SaveNaInspectieResponse>> {
    return this.callApexRest<SalesforceApiResponse<SaveNaInspectieResponse>>(
      `/v1/field/items/${itemId}/na-inspection`,
      'POST',
      data
    );
  }

  // ============================================================================
  // Self-Service (Zelf Halen & Brengen) Methods
  // ============================================================================

  /**
   * Get work orders for self-service pickup/delivery
   * @param filter - 'ophalen' (customer picks up) or 'brengen' (customer brings)
   * @param search - Optional search query (customer name, WO number)
   */
  async getSelfServiceWorkOrders(
    filter?: SelfServiceFilter,
    search?: string
  ): Promise<SalesforceApiResponse<SelfServiceWorkOrdersResponse>> {
    const params = new URLSearchParams();
    if (filter) params.set('filter', filter);
    if (search) params.set('search', search);
    const queryString = params.toString();
    const endpoint = queryString
      ? `/v1/field/self-service/workorders?${queryString}`
      : '/v1/field/self-service/workorders';

    return this.callApexRest<SalesforceApiResponse<SelfServiceWorkOrdersResponse>>(endpoint);
  }

  /**
   * Complete a self-service work order
   * - 'Wordt opgehaald' -> 'Afgerond' (customer picked up cleaned items)
   * - 'Wordt gebracht' -> 'Zelf gebracht' (customer brought items for cleaning)
   * @param workOrderId - The Werk_Order__c Id
   */
  async completeSelfServiceWorkOrder(
    workOrderId: string
  ): Promise<SalesforceApiResponse<CompleteSelfServiceResponse>> {
    return this.callApexRest<SalesforceApiResponse<CompleteSelfServiceResponse>>(
      `/v1/field/self-service/workorders/${workOrderId}/complete`,
      'POST',
      {}
    );
  }

  // ============================================================================
  // Inpakken (Webshop Order Packing) Methods
  // ============================================================================

  /**
   * Get webshop orders waiting to be packed (status = 'Klaarmaken verzending').
   */
  async getInpakkenOrders(): Promise<SalesforceApiResponse<WebshopInpakkenOrdersResponse>> {
    return this.callApexRest<SalesforceApiResponse<WebshopInpakkenOrdersResponse>>(
      '/v1/field/inpakken/orders'
    );
  }

  /**
   * Lookup a webshop order by its MyParcel tracking number (barcode-scan).
   */
  async getInpakkenOrderByTracking(
    trackingNumber: string
  ): Promise<SalesforceApiResponse<WebshopInpakkenOrder>> {
    return this.callApexRest<SalesforceApiResponse<WebshopInpakkenOrder>>(
      `/v1/field/inpakken/orders/by-tracking/${encodeURIComponent(trackingNumber)}`
    );
  }

  /**
   * Mark a webshop order as packed. Flips status from 'Klaarmaken verzending' to 'Ingepakt'.
   */
  async markOrderPacked(
    orderId: string
  ): Promise<SalesforceApiResponse<MarkPackedResponse>> {
    return this.callApexRest<SalesforceApiResponse<MarkPackedResponse>>(
      `/v1/field/inpakken/orders/${orderId}/pack`,
      'POST',
      {}
    );
  }
}

// Export singleton instance
export const salesforceClient = new SalesforceClient();

// Export class for testing
export { SalesforceClient };
